import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, Download, Clock, FileText, CheckCheck } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
    CALIF_BADGE, fmtDate, diasRestantes,
} from "./_common";

const ESTADO_DICT = {
    vigente: "bg-emerald-100 text-emerald-800 border-emerald-300",
    caducado: "bg-gray-100 text-gray-600 border-gray-200",
    utilizado: "bg-blue-100 text-blue-800 border-blue-300",
};

export default function Dictamenes() {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filtroEstado, setFiltroEstado] = useState("__all");
    const [filtroCalif, setFiltroCalif] = useState("__all");

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filtroEstado !== "__all") params.estado = filtroEstado;
            if (filtroCalif !== "__all") params.calificacion_global = filtroCalif;
            const { data } = await api.get("/aceptacion/dictamenes", { params });
            setRows(data || []);
        } catch (err) {
            toast.error("No se pudieron cargar los dictámenes");
        } finally { setLoading(false); }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtroEstado, filtroCalif]);

    const descargarPDF = (id, folio) => {
        window.open(`${api.defaults.baseURL}/aceptacion/dictamenes/${id}/pdf`, "_blank");
    };

    const marcarUtilizado = async (id) => {
        try {
            await api.post(`/aceptacion/dictamenes/${id}/marcar-utilizado`);
            toast.success("Dictamen marcado como utilizado");
            load();
        } catch (err) {
            toast.error("No se pudo marcar", {
                description: err?.response?.data?.detail,
            });
        }
    };

    const diasColor = (iso) => {
        const d = diasRestantes(iso);
        if (d === null) return "text-gray-400";
        if (d <= 0) return "text-red-600 font-semibold";
        if (d <= 5) return "text-red-600 font-semibold";
        if (d <= 15) return "text-amber-700";
        return "text-emerald-700";
    };

    return (
        <div className="p-8 lg:p-10 space-y-6 max-w-[1600px]" data-testid="dictamenes-page">
            <div className="flex items-start justify-between gap-6 flex-wrap">
                <div>
                    <h1 className="font-display text-3xl text-[color:var(--brand-charcoal)]">
                        Dictámenes F-09
                    </h1>
                    <p className="text-sm text-[color:var(--brand-charcoal)]/70 mt-1">
                        Dictámenes del Comité Fiduciario. Vigencia 90 días naturales desde la emisión.
                    </p>
                </div>
                <Button variant="outline" onClick={load} disabled={loading}>
                    <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                    Refrescar
                </Button>
            </div>

            <div className="flex gap-3 flex-wrap items-center">
                <Select value={filtroEstado} onValueChange={setFiltroEstado}>
                    <SelectTrigger className="w-56" data-testid="filtro-estado">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__all">Todos los estados</SelectItem>
                        <SelectItem value="vigente">Vigente</SelectItem>
                        <SelectItem value="caducado">Caducado</SelectItem>
                        <SelectItem value="utilizado">Utilizado</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={filtroCalif} onValueChange={setFiltroCalif}>
                    <SelectTrigger className="w-44">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__all">Toda calificación</SelectItem>
                        <SelectItem value="A">A — favorable</SelectItem>
                        <SelectItem value="B">B — con observaciones</SelectItem>
                        <SelectItem value="C">C — desfavorable</SelectItem>
                    </SelectContent>
                </Select>
                <div className="ml-auto text-sm text-gray-500">{rows.length} dictámenes</div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-[color:var(--brand-charcoal)] text-[color:var(--brand-paper)]">
                        <tr>
                            <th className="text-left px-4 py-3 text-xs uppercase tracking-wider">Folio</th>
                            <th className="text-left px-4 py-3 text-xs uppercase tracking-wider">Solicitud</th>
                            <th className="text-left px-4 py-3 text-xs uppercase tracking-wider">Emisión</th>
                            <th className="text-left px-4 py-3 text-xs uppercase tracking-wider">Vigencia hasta</th>
                            <th className="text-center px-4 py-3 text-xs uppercase tracking-wider">Días restantes</th>
                            <th className="text-center px-4 py-3 text-xs uppercase tracking-wider">Calif.</th>
                            <th className="text-left px-4 py-3 text-xs uppercase tracking-wider">Resolución</th>
                            <th className="text-center px-4 py-3 text-xs uppercase tracking-wider">Estado</th>
                            <th className="text-right px-4 py-3 text-xs uppercase tracking-wider">Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={9} className="text-center text-gray-500 py-12">
                                    {loading ? "Cargando..." : "No hay dictámenes con estos filtros"}
                                </td>
                            </tr>
                        )}
                        {rows.map((r, i) => (
                            <tr key={r.id}
                                className={`border-t border-gray-100 hover:bg-[color:var(--brand-paper)]/50 cursor-pointer ${i % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}
                                onClick={() => navigate(`/aceptacion/dictamenes/${r.folio || r.id}`)}
                                data-testid={`dict-row-${r.folio || r.id}`}
                            >
                                <td className="px-4 py-3 font-mono text-xs">{r.folio}</td>
                                <td className="px-4 py-3 font-mono text-xs">{r.folio_f01 || r.solicitud_id}</td>
                                <td className="px-4 py-3 text-xs">{fmtDate(r.fecha_emision || r.created_at)}</td>
                                <td className="px-4 py-3 text-xs">{fmtDate(r.fecha_vigencia_hasta)}</td>
                                <td className={`px-4 py-3 text-center text-xs ${diasColor(r.fecha_vigencia_hasta)}`}>
                                    {r.estado === "vigente" ? `${diasRestantes(r.fecha_vigencia_hasta) || 0}d` : "—"}
                                </td>
                                <td className="px-4 py-3 text-center">
                                    <span className={`inline-block w-7 h-7 rounded-full text-sm font-bold leading-7 border ${CALIF_BADGE[r.calificacion_global] || "bg-gray-100"}`}>
                                        {r.calificacion_global}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-xs capitalize">{r.resolucion?.replace(/_/g, " ")}</td>
                                <td className="px-4 py-3 text-center">
                                    <span className={`text-xs px-2 py-0.5 rounded border ${ESTADO_DICT[r.estado] || "bg-gray-100"}`}>
                                        {r.estado}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-right space-x-1">
                                    <Button size="sm" variant="ghost" onClick={(e) => {
                                        e.stopPropagation();
                                        descargarPDF(r.id, r.folio);
                                    }} title="Descargar PDF">
                                        <Download className="h-3.5 w-3.5" />
                                    </Button>
                                    {r.estado === "vigente" && (
                                        <Button size="sm" variant="ghost" onClick={(e) => {
                                            e.stopPropagation();
                                            marcarUtilizado(r.id);
                                        }} title="Marcar utilizado">
                                            <CheckCheck className="h-3.5 w-3.5" />
                                        </Button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
